// @ts-nocheck
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import {
  Avatar,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { ROUTES } from "../constants/routes";
import { EditModeProvider } from "../context/EditModeContext";
import { CVSidebar } from "../features/CVEditor/components/CVSidebar";

import { Person } from "../models/person.model";
import { GraphService } from "../services/graphService";
import { ImportReport, ImportService } from "../services/importService";
import { PersonService } from "../services/personService";

// CV sections that will appear in sidebar navigation for new CV
const CV_SECTIONS = [{ id: "personal-details", label: "Personal Details" }];

// Main component with EditModeProvider
interface CVEditorNewProps {
  autoFillUserData?: boolean; // Whether to auto-fill user data from Graph API
}

export function CVEditorNew({ autoFillUserData = true }: CVEditorNewProps) {
  return (
    <EditModeProvider defaultEditMode={true}>
      <CVEditorNewContent autoFillUserData={autoFillUserData} />
    </EditModeProvider>
  );
}

// Inner component that uses the EditModeContext
function CVEditorNewContent({
  autoFillUserData,
}: {
  autoFillUserData: boolean;
}) {
  const { userRole, isAdmin, isManager } = useAuth();
  const canEditEmail = isAdmin || isManager;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("personal-details");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [emailExists, setEmailExists] = useState(false);
  const [existingPersonInfo, setExistingPersonInfo] = useState<{
    personId: number | null;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [userData, setUserData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    image?: string | null;
    officeLocation?: string;
  }>({
    firstName: "",
    lastName: "",
    email: "",
    image: null,
    officeLocation: "",
  });
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const personService = new PersonService();
  const graphService = new GraphService();
  const importService = new ImportService();

  // Fetch user data from Graph API for regular users or when autoFillUserData is true
  useEffect(() => {
    const fetchUserInfo = async () => {
      // Only fetch user data if autoFillUserData is true
      // Removed the userRole restriction to allow any user role to get their data filled
      if (autoFillUserData) {
        setLoading(true);
        try {
          const profileData = await graphService.getSelectedUserInfo();
          console.log("Profile data fetched:", profileData);

          // Extract first name and last name from display name (format: lastname firstname)
          if (profileData.displayName) {
            const nameParts = profileData.displayName.split(" ");
            // Assuming format is "lastname firstname", so we swap them
            const lastName = nameParts[0] || "";
            const firstName = nameParts.slice(1).join(" ") || "";

            setUserData({
              firstName,
              lastName,
              email: profileData.mail || profileData.userPrincipalName || "",
              image: null,
              officeLocation: profileData.officeLocation || "",
            });

            // Try to get user photo as well
            try {
              const photoBase64 =
                await graphService.getCurrentUserPhoto();
              if (photoBase64) {
                setUserData((prev) => ({ ...prev, image: photoBase64 }));
              }
            } catch (photoError) {
              console.warn("Could not fetch user photo:", photoError);
            }
          } else {
            console.warn("No display name found in profile data");
            setError(
              "Could not retrieve your name from Microsoft Graph. Please fill in the details manually."
            );
          }
        } catch (error) {
          console.error("Error fetching user profile data:", error);
          setError(
            "Failed to fetch user information from Microsoft Graph. Please fill in the details manually."
          );
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserInfo();
  }, [autoFillUserData]); // Removed userRole dependency

  const handleInputChange = (field: string, value: string) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
    
    // If email field changed, check if it exists and reset overwrite confirmation
    if (field === 'email') {
      setConfirmOverwrite(false);
      checkEmailExists(value);
    }
  };

  // Check if email already exists in the system
  const checkEmailExists = async (email: string) => {
    // Reset state
    setEmailExists(false);
    setExistingPersonInfo(null);
    
    // Validate email format first
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      return;
    }
    
    setCheckingEmail(true);
    try {
      const result = await importService.checkEmailExists(email);
      if (result.exists) {
        setEmailExists(true);
        setExistingPersonInfo({
          personId: result.personId,
          firstName: result.firstName,
          lastName: result.lastName,
        });
      } else {
        setEmailExists(false);
        setExistingPersonInfo(null);
      }
    } catch (error) {
      console.error('Error checking email existence:', error);
      // Don't show error to user, just continue
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSave = async () => {
    // Validate form
    if (
      !userData.firstName.trim() ||
      !userData.lastName.trim() ||
      !userData.email.trim()
    ) {
      setError(
        "Please fill in all required fields (First Name, Last Name, Email, and Profile Photo)."
      );
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Image validation - require a profile photo
    if (!userData.image || userData.image.trim() === "") {
      setError("Please upload a profile photo or use your Microsoft account photo.");
      return;
    }

    // Feature 8.2: Check if email exists and user hasn't confirmed overwrite
    if (emailExists && !confirmOverwrite) {
      setError(
        `A CV already exists for this email address${existingPersonInfo?.firstName ? ` (${existingPersonInfo.firstName} ${existingPersonInfo.lastName})` : ''}. Please confirm that you want to overwrite it.`
      );
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let personId: number;
      let importReport: ImportReport | null = null;

      if (uploadedFile) {
        // Use intelligent import with form data as source of truth
        const response = await importService.intelligentImport(
          uploadedFile,
          userData.firstName.trim(),
          userData.lastName.trim(),
          userData.email.trim(),
          userData.image,
          userData.officeLocation?.trim(),
          emailExists ? confirmOverwrite : undefined
        );
        personId = response.personId;
        importReport = response.report;
      } else {
        // Use regular create person endpoint
        const personData: Person = {
          id: 0,
          firstName: userData.firstName.trim(),
          lastName: userData.lastName.trim(),
          email: userData.email.trim(),
          image: userData.image || "",
          imagePath: "",
          imageName: "",
          inactive: false,
          cvUpdateDate: new Date(),
          officeLocationName: userData.officeLocation?.trim() || "",
        };

        const createdPerson = await personService.createPerson(personData);
        personId = createdPerson.id;
      }

      // Show success notification
      notifications.show({
        title: "Success",
        message: "CV created successfully!",
        color: "green",
      });

      // Navigate to the edit page for the new CV, passing import report via state
      navigate(ROUTES.CV_EDITOR_WITH_ID(personId), {
        state: { importReport },
      });
    } catch (error) {
      console.error("Error creating CV:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create CV. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = (base64Image: string) => {
    setUserData((prev) => ({ ...prev, image: base64Image }));
  };

  const handlePhotoRemove = () => {
    setUserData((prev) => ({ ...prev, image: null }));
  };

  const handleUseAccountPhoto = async () => {
    try {
      const photo = await graphService.getCurrentUserPhoto();
      if (photo) {
        setUserData((prev) => ({ ...prev, image: photo }));
      } else {
        notifications.show({
          title: "No photo found",
          message: "No profile photo found.",
          color: "yellow",
        });
      }
    } catch (error) {
      console.error("Error fetching Microsoft account photo:", error);
      notifications.show({
        title: "Error",
        message: "Failed to fetch Microsoft account photo.",
        color: "red",
      });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setError(null);

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Photo size exceeds 5MB limit. Please choose a smaller image.");
      notifications.show({
        title: "File too large",
        message: "Photo size exceeds 5MB limit. Please choose a smaller image.",
        color: "red",
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file (JPEG, PNG, etc.)");
      notifications.show({
        title: "Invalid file type",
        message: "Please select a valid image file (JPEG, PNG, etc.)",
        color: "red",
      });
      return;
    }

    notifications.show({
      title: "Processing image",
      message: "Please wait while we process your photo...",
      loading: true,
      autoClose: false,
      withCloseButton: false,
      id: "processing-photo",
    });

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target?.result as string;
      handlePhotoChange(base64Image);

      // Dismiss the loading notification
      notifications.hide("processing-photo");
      notifications.show({
        title: "Success",
        message: "Photo uploaded successfully",
        color: "green",
      });
    };
    reader.onerror = () => {
      setError("Failed to read the image file. Please try again.");

      // Dismiss the loading notification
      notifications.hide("processing-photo");
      notifications.show({
        title: "Error",
        message: "Failed to read the image file. Please try again.",
        color: "red",
      });
    };
    reader.readAsDataURL(file);

    // Reset file input
    event.target.value = "";
  };

  // Reference to hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Trigger file input click
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Handle scrolling to sections
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  // Handle file drop for intelligent import
  const handleFileDrop = (files: File[]) => {
    if (files.length > 0) {
      setUploadedFile(files[0]);
      setError(null);
    }
  };

  // Handle file rejection
  const handleFileReject = () => {
    setError("Please upload a valid CV file (PDF or DOCX, max 10MB)");
    notifications.show({
      title: "Invalid file",
      message: "Please upload a valid CV file (PDF or DOCX, max 10MB)",
      color: "red",
    });
  };


  // Remove uploaded file
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setError(null);
  };

  if (saving && uploadedFile) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
        }}
      >
        <Stack align="center" spacing="xl">
          <Loader size="xl" />
          <Stack align="center" spacing="md">
            <Text size="xl" fw={600}>
              Processing your CV
            </Text>
            <Text size="md" c="dimmed" ta="center" maw={500}>
              We're analyzing your CV file. This process may take several minutes (up to 10 minutes) depending on the document size and complexity.
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={500}>
              Please do not close this page or navigate away while processing is in progress.
            </Text>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar with navigation */}
        <CVSidebar
          activeSection={activeSection}
          onSectionClick={scrollToSection}
          sections={CV_SECTIONS}
        />

        {/* Main content area */}
        <Box
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "32px",
            height: "100%",
            position: "relative",
          }}
        >
          <Stack gap="lg">
            {/* Personal details section */}
            <Paper p="xl" radius="md" withBorder mb="xl" id="personal-details">
              <Stack spacing="xl">
                <Group position="apart">
                  <Text size="xl" fw={700} color="dark">
                    {autoFillUserData ? "Create My CV" : "Create New CV"}
                  </Text>
                </Group>

                {error && (
                  <Box
                    mb="md"
                    p="sm"
                    style={{
                      backgroundColor: theme.colors.red[0],
                      borderRadius: theme.radius.sm,
                    }}
                  >
                    <Text color="red">{error}</Text>
                  </Box>
                )}

                {loading ? (
                  <Center py="xl">
                    <Stack align="center" spacing="md">
                      <Loader size="md" />
                      <Text c="dimmed">Loading user information...</Text>
                    </Stack>
                  </Center>
                ) : (
                  <>
                    <Text mb="md" size="sm">
                      Please fill in the basic information below to create your
                      CV. A professional photo is required - you can upload your own or use your Microsoft
                      account photo. You can optionally upload a CV file (PDF or DOCX) to automatically extract additional information.
                    </Text>

                    {autoFillUserData ? (
                      <Box
                        p="sm"
                        mb="md"
                        style={{
                          backgroundColor: theme.colors.blue[0],
                          borderRadius: theme.radius.sm,
                        }}
                      >
                        <Text color="blue">
                          Your profile information has been pre-filled from your
                          Microsoft account. Feel free to review and update it
                          before creating your CV.
                        </Text>
                      </Box>
                    ) : (
                      <Box
                        p="sm"
                        mb="md"
                        style={{
                          backgroundColor: theme.colors.gray[0],
                          borderRadius: theme.radius.sm,
                        }}
                      >
                        <Text color="dimmed">
                          Creating a new CV with empty fields. Please fill in
                          all required information.
                        </Text>
                      </Box>
                    )}

                    {uploadedFile && (
                      <Box
                        p="md"
                        mb="md"
                        style={{
                          backgroundColor: theme.colors.blue[0],
                          borderRadius: theme.radius.sm,
                        }}
                      >
                        <Text size="sm" c="blue" fw={500}>
                          ⏱️ CV file uploaded. Processing may take up to 3 minutes. Your form data (name, email, etc.) will be used as the source of truth, and additional information will be extracted from the CV file.
                        </Text>
                      </Box>
                    )}

                    <Stack spacing="md">
                          <Divider label="CV File Upload (Optional)" labelPosition="center" />

                          <Text size="sm" c="dimmed">
                            Optionally upload your CV file (PDF or DOCX) to automatically extract work experience, education, certifications, and other details. Your personal information (name, email, photo) from the form above will be used.
                          </Text>

                          {!uploadedFile ? (
                            <Dropzone
                              onDrop={handleFileDrop}
                              onReject={handleFileReject}
                              maxSize={10 * 1024 * 1024}
                              accept={[MIME_TYPES.pdf, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                              disabled={saving}
                              style={{
                                minHeight: 150,
                                borderWidth: 2,
                                borderStyle: 'dashed',
                                borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4],
                                backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0],
                                cursor: 'pointer',
                              }}
                            >
                              <Center>
                                <Stack align="center" spacing="xs">
                                  <ArrowUpTrayIcon style={{ width: 48, height: 48, color: isDark ? theme.colors.dark[2] : theme.colors.gray[5] }} />
                                  <Text size="lg" fw={500}>
                                    Drop your CV here or click to browse
                                  </Text>
                                  <Text size="sm" c="dimmed" align="center">
                                    Supported formats: PDF, DOCX (max 10MB)
                                  </Text>
                                </Stack>
                              </Center>
                            </Dropzone>
                          ) : (
                            <Paper
                              p="md"
                              withBorder
                              style={{
                                backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0]
                              }}
                            >
                              <Group position="apart">
                                <Group>
                                  <DocumentTextIcon style={{ width: 32, height: 32, color: theme.colors.blue[6] }} />
                                  <Stack spacing={0}>
                                    <Text fw={500}>{uploadedFile.name}</Text>
                                    <Text size="sm" c="dimmed">
                                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                                    </Text>
                                  </Stack>
                                </Group>
                                <Button
                                  variant="subtle"
                                  color="red"
                                  leftSection={<XMarkIcon style={{ width: 16, height: 16 }} />}
                                  onClick={handleRemoveFile}
                                  disabled={saving}
                                >
                                  Remove
                                </Button>
                              </Group>
                            </Paper>
                          )}

                      <Divider label="Personal Information" labelPosition="center" />

                      <Stack spacing="md">
                          <Group
                            align="flex-start"
                            spacing="xl"
                            position="apart"
                            style={{ flexWrap: "wrap" }}
                          >
                            <Box w={220} mx="auto" mb="md">
                              <Stack spacing="xs" align="center">
                                <Avatar
                                  src={userData.image}
                                  size={120}
                                  radius={120}
                                  styles={{
                                    root: {
                                      border: !userData.image ? `2px solid ${theme.colors.red[5]}` : "none",
                                    },
                                    image: { objectFit: "cover" },
                                    placeholder: {
                                      backgroundColor: !userData.image ? theme.colors.red[0] : theme.colors.gray[1],
                                      color: !userData.image ? theme.colors.red[6] : theme.colors.gray[6],
                                      fontSize: "2rem",
                                    },
                                  }}
                                  alt={
                                    userData.firstName
                                      ? `${userData.firstName} ${userData.lastName}`
                                      : "Profile photo"
                                  }
                                >
                                  {!userData.image &&
                                    (userData.firstName
                                      ? userData.firstName[0] +
                                      (userData.lastName
                                        ? userData.lastName[0]
                                        : "")
                                      : "CV")}
                                </Avatar>
                                <Text
                                  size="xs"
                                  color="dimmed"
                                  align="center"
                                  mt={-5}
                                >
                                  Photo: 150×150px{" "}
                                  <Text component="span" color="red" fw={500}>
                                    *
                                  </Text>
                                </Text>
                                <Group spacing="xs" position="center" wrap="nowrap">
                                  <Button
                                    size="compact-sm"
                                    variant="light"
                                    onClick={handleUseAccountPhoto}
                                    disabled={saving}
                                    fullWidth
                                  >
                                    Use Profile Photo
                                  </Button>
                                  <Button
                                    size="compact-sm"
                                    variant="light"
                                    onClick={triggerFileUpload}
                                    disabled={saving}
                                    fullWidth
                                  >
                                    Upload Photo
                                  </Button>
                                </Group>
                                <Button
                                  size="compact-sm"
                                  variant="light"
                                  color="pink"
                                  onClick={handlePhotoRemove}
                                  disabled={!userData.image || saving}
                                  fullWidth
                                >
                                  Remove
                                </Button>
                                {!userData.image && (
                                  <Text
                                    size="xs"
                                    color="red"
                                    align="center"
                                    mt="xs"
                                  >
                                    Profile photo is required
                                  </Text>
                                )}
                                {/* Hidden file input for photo upload */}
                                <input
                                  type="file"
                                  ref={fileInputRef}
                                  style={{ display: "none" }}
                                  accept="image/*"
                                  onChange={handleFileUpload}
                                  disabled={saving}
                                />
                              </Stack>
                            </Box>

                            <Box style={{ flex: 1, minWidth: 300 }}>
                              <Stack spacing="md">
                                <TextInput
                                  label="First Name"
                                  placeholder="Enter first name"
                                  value={userData.firstName}
                                  onChange={(e) =>
                                    handleInputChange("firstName", e.target.value)
                                  }
                                  required
                                  disabled={saving}
                                  withAsterisk
                                />

                                <TextInput
                                  label="Last Name"
                                  placeholder="Enter last name"
                                  value={userData.lastName}
                                  onChange={(e) =>
                                    handleInputChange("lastName", e.target.value)
                                  }
                                  required
                                  disabled={saving}
                                  withAsterisk
                                />

                                <TextInput
                                  label="Email"
                                  placeholder="Enter email address"
                                  value={userData.email}
                                  onChange={(e) =>
                                    handleInputChange("email", e.target.value)
                                  }
                                  required
                                  disabled={saving || !canEditEmail}
                                  withAsterisk
                                  rightSection={checkingEmail ? <Loader size="xs" /> : null}
                                  description={!canEditEmail ? "Email is set from your Microsoft account" : undefined}
                                />

                                {/* Feature 8.2: Existing CV Overwrite Warning */}
                                {emailExists && existingPersonInfo && (
                                  <Box
                                    p="md"
                                    style={{
                                      backgroundColor: theme.colors.orange[0],
                                      borderRadius: theme.radius.sm,
                                      border: `1px solid ${theme.colors.orange[3]}`,
                                    }}
                                  >
                                    <Stack spacing="sm">
                                      <Text fw={600} color="orange.7" size="sm">
                                        ⚠️ CV Already Exists
                                      </Text>
                                      <Text size="sm" color="orange.9">
                                        A CV already exists for this email address 
                                        {existingPersonInfo.firstName && existingPersonInfo.lastName && (
                                          <strong> ({existingPersonInfo.firstName} {existingPersonInfo.lastName})</strong>
                                        )}
                                        . Importing will <strong>overwrite</strong> the existing CV and all its data will be replaced. Previous data will be irreversibly deleted.
                                      </Text>
                                      <Checkbox
                                        label="I understand that this will overwrite the existing CV"
                                        checked={confirmOverwrite}
                                        onChange={(e) => setConfirmOverwrite(e.currentTarget.checked)}
                                        disabled={saving}
                                        styles={{
                                          label: { color: theme.colors.orange[9], fontWeight: 500 },
                                        }}
                                      />
                                    </Stack>
                                  </Box>
                                )}

                                <TextInput
                                  label="Office Location"
                                  placeholder="Enter office location (e.g., Katowice, Warsaw)"
                                  value={userData.officeLocation || ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "officeLocation",
                                      e.target.value
                                    )
                                  }
                                  disabled={saving}
                                />
                              </Stack>
                            </Box>
                          </Group>

                        <Text mt="xl" size="sm" c="dimmed">
                          Fill in all required details above (including a profile photo) to create a new CV. {uploadedFile ? "If you uploaded a CV file, additional information will be extracted automatically." : "After saving, you will be able to add more sections like work experience, education, etc."}
                        </Text>

                        <Divider my="md" />

                        <Group position="right">
                          <Button
                            variant="default"
                            onClick={() => navigate(-1)}
                            disabled={saving}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSave}
                            loading={saving}
                            color="brand"
                          >
                            {autoFillUserData ? "Create My CV" : "Create New CV"}
                          </Button>
                        </Group>
                      </Stack>
                    </Stack>
                    </>
                  )}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default CVEditorNew;
